import axios from "axios";
import {
  getTokenHeader,
  escapeHTML,
  initGlobalCopyToClipboard
} from "@/helpers/commonHelper";
import { getPrincipalsWithAdZoneInAppdinxLogin } from "@/helpers/adManagerHelper";
import { updateAllNewsInApp } from "@/helpers/newsHelper";
import { debounce } from "lodash";
import VuePrism from "vue-prism";
import "prismjs/themes/prism-okaidia.min.css";
import TreeViewSelection from "@/components/Common/TreeViewSelection/TreeViewSelection.vue";
const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

export default {
  name: "AdManagerAdvertisingZoneEditDialog",
  components: {
    TreeViewSelection,
    VuePrism
  },
  async mounted() {
    const ignorePrincipalId = Number(!!this.isAdmin);
    await this.$store.dispatch("getNewsApiKeys", { ignorePrincipalId }); // News
    await this.$store.dispatch("getApiKeys", { ignorePrincipalId }); // POI
    await this.$store.dispatch("getEventsApiKeys", { ignorePrincipalId }); // Events
    await this.$store.dispatch("getAdManagerApiKeys", { ignorePrincipalId }); // Events
    this.initGlobalCopyToClipboard();
    window.Prism.highlightAll();
  },
  props: {
    dialog: { type: Boolean, default: false },
    editedItem: { type: Object, default: null },
    currentPrincipal: { type: String, default: "" },
    formats: { type: Array, default: () => [] },
    placementOptions: { type: Object, default: () => {} },
    targetOptions: { type: Array, default: () => [] }
  },
  data() {
    return {
      innerDialog: false,
      localItem: this.blank(),
      initialItem: {},
      isLoadingZones: false,
      valid: false,
      saving: false,
      mainPeacock: "#00636C",
      req: v => !!v || "Pflichtfeld"
    };
  },
  watch: {
    dialog(val) {
      this.innerDialog = val;
      if (val) {
        this.fillData();
        this.setCodePreview();
      }
    },
    "localItem.api_key"() {
      if (this.showCodeSnippet) {
        this.setCodePreview();
      }
    },
    "localItem.placement"() {
      if (this.showCodeSnippet) {
        this.setCodePreview();
      }
    },
    "localItem.target"() {
      if (this.showCodeSnippet) {
        this.setCodePreview();
        this.localItem.placement = null;
      } else {
        this.localItem.placement =
          this.localItem.placement ?? this.cPlacementOptions[0].value;
      }
    },
    options: debounce(function() {
      this.loadZones();
    }, 150)
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    isAdmin() {
      return this.currentUser?.scopes?.includes("role:admin");
    },
    principals() {
      return this.$store.state.principals;
    },
    newsApiKeys() {
      const apiKeys = this.$store.state.newsApiKeys;
      if (!this.isAdmin) {
        return apiKeys;
      }
      const grouped = this.groupApiKeysByPrincipal(apiKeys);
      return grouped;
    },
    poiApiKeys() {
      const apiKeys = this.$store.state.apiKeys;
      if (!this.isAdmin) {
        return apiKeys;
      }
      const grouped = this.groupApiKeysByPrincipal(apiKeys);
      return grouped;
    },
    eventApiKeys() {
      const apiKeys = this.$store.state.eventsApiKeys;
      if (!this.isAdmin) {
        return apiKeys;
      }
      const grouped = this.groupApiKeysByPrincipal(apiKeys);
      return grouped;
    },
    adManagerApiKeys() {
      const apiKeys = this.$store.state.adManagerApiKeys;
      if (!this.isAdmin) {
        return apiKeys;
      }
      const grouped = this.groupApiKeysByPrincipal(apiKeys);
      return grouped;
    },
    cApiKeys() {
      if (!this.localItem?.target) {
        return [];
      }
      switch (this.localItem.target) {
        case "website":
        case "app":
          return this.adManagerApiKeys;
        case "news_widget":
          return this.newsApiKeys;
        case "pois_widget":
          return this.poiApiKeys;
        case "events_widget":
          return this.eventApiKeys;
        default:
          throw new Error(`Unrecognized target: ${this.localItem.target}`);
      }
    },
    cPlacementOptions() {
      return this.placementOptions[this.localItem.target];
    },
    showCodeSnippet() {
      return this.localItem.target === "website";
    },
    updateInApp() {
      if (this.localItem.target === "app") {
        const keys = [
          "active",
          "format",
          "display_is_advert_information",
          "api_key",
          "placement"
        ];
        if (keys.some(key => this.localItem[key] !== this.initialItem[key])) {
          return true;
        }
      } else if (this.initialItem.target === "app") {
        return true;
      }
      return false;
    }
  },
  methods: {
    resetApiKey() {
      this.localItem.api_key = null;
    },
    setApiKey(value) {
      if (Array.isArray(value)) {
        value = value[0];
      }

      this.$set(this.localItem, "api_key", value);
      this.$forceUpdate();
    },
    groupApiKeysByPrincipal(apiKeys) {
      const ret = [];
      for (const principal of this.principals) {
        const children =
          apiKeys?.filter(apiKey => apiKey.config_ref === principal.id) ?? [];
        if (!children?.length) {
          continue;
        }
        ret.push({
          title: principal.name,
          children: children.map(c => ({
            title: this.apiKeyText(c),
            _id: c.apiKey
          }))
        });
      }
      return ret;
    },
    initGlobalCopyToClipboard,
    escapeHTML,
    blank() {
      return {
        _id: "",
        principal_id: this.currentPrincipal,
        name: "",
        format: null,
        placement: null,
        rotation_enabled: false,
        rotation_seconds: 4,
        active: true,
        api_key: null,
        display_is_advert_information: true
      };
    },
    apiKeyText(k) {
      const name = k?.description;
      const apiKey = k?.apiKey;
      return `${name} - ${apiKey}`;
    },
    async loadZones() {
      if (this.isLoadingZones) return; // Guard gegen parallele Aufrufe
      this.isLoadingZones = true;
      this.loading = true;
      const headers = getTokenHeader();
      try {
        const r = await axios.get(
          env.ad_manager_api_client_url + "ad/zones", // ggf. "api/ad/zones"
          { headers, params: this.getSearchParams() }
        );
        this.zones = r.data || [];
        const count = parseInt(r?.headers?.["x-count"]);
        this.total = isNaN(count) ? this.zones.length : count;
      } catch (e) {
        // NICHT re-throwen -> sonst Uncaught (in promise)
        console.error(
          "Zonen laden fehlgeschlagen:",
          e?.response?.status,
          e?.message
        );
        this.$store?.dispatch(
          "createError",
          "Werbezonen konnten nicht geladen werden (" +
            (e?.response?.status || "") +
            ")."
        );
        this.zones = [];
        this.total = 0;
      } finally {
        this.loading = false;
        this.isLoadingZones = false;
      }
    },
    fillData() {
      const base = this.editedItem
        ? JSON.parse(JSON.stringify(this.editedItem))
        : this.blank();
      base.principal_id = this.currentPrincipal;
      this.localItem = base;
      this.initialItem = window.structuredClone(this.localItem);
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    async onSave() {
      const ok = await this.$refs.form.validate();
      if (!ok) return;

      this.saving = true;
      const headers = getTokenHeader();
      const payload = {
        ...this.localItem,
        principal_id: this.currentPrincipal
      };
      if (!payload._id) delete payload._id;

      try {
        if (this.localItem._id) {
          await axios.put(
            env.ad_manager_api_client_url + "ad/zones/" + this.localItem._id,
            payload,
            { headers }
          );
        } else {
          const r = await axios.post(
            env.ad_manager_api_client_url + "ad/zones",
            payload,
            { headers }
          );
          this.localItem._id = r.data?._id || this.localItem._id;
        }
        if (this.updateInApp) {
          getPrincipalsWithAdZoneInAppdinxLogin(this.localItem._id).forEach(
            principal => {
              updateAllNewsInApp(principal.id, headers);
            }
          );
        }
        this.$emit("afterSave");
        this.onCancel();
      } catch (e) {
        console.error(e);
        await this.$store.dispatch("createError", "Speichern fehlgeschlagen");
      } finally {
        this.saving = false;
      }
    },
    onCancel() {
      this.$emit("close");
      this.$emit("update:dialog", false);
    },
    getPreviewOuterHTML(innerHTML) {
      const randomId = (Math.random() + 1).toString(36).substring(7);
      const copyButtonHTML = `<p class="copy-code-preview"
      onClick="copyCodePreviewToClipboard('${randomId}');"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M19 21H8V7h11m0-2H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2m-3-4H4a2 2 0 0 0-2 2v14h2V3h12V1Z"/></svg>
        <span>Kopieren</span>
      </p>`;
      return `${copyButtonHTML}<pre class="language-markup" id="${randomId}"><code>${this.escapeHTML(
        innerHTML
      )}</code></pre>`;
    },
    setCodePreview: debounce(function() {
      const codePreviewContainer = document.querySelector(
        ".widget-code-preview"
      );
      if (!codePreviewContainer) {
        return;
      }
      const codePreviewElement = codePreviewContainer.querySelector("pre");

      // prettier-ignore
      const innerHTML = `
<!-- Skript für eo-ad-zone laden -->
<script src="${env.ad_manager_ad_zone_widget_api_url}eo-ad-zone.js" type="text/javascript"></script>

<!-- Eigentliche Werbezone an gewünschter Stelle platzieren -->
<eo-ad-zone
    data-apikey="${this.localItem.api_key ?? ""}"
    data-format="desktop">
</eo-ad-zone>

<!-- Optionales Skript für Wechsel zwischen Format Desktop und Mobil -->
<script>
    (function(){
      const adZone = document.querySelector('eo-ad-zone[data-apikey="${this.localItem.api_key ?? ""}"]');
      if (!adZone) {
        console.error('Could not find eo-ad-zone element.');
        return;
      }
      
      function setAdZoneFormat() {
          // Mobile-Wert nach Belieben anpassen.
          const mobileThreshold = 960;
          
          const format = window.innerWidth <= mobileThreshold ? 'mobile' : 'desktop';
          if (adZone.getAttribute('data-format') !== format) {
              adZone.setAttribute('data-format', format);
          }        
      }
        
      window.addEventListener('resize', setAdZoneFormat);
      setAdZoneFormat();
    })();
</script>`;

      if (codePreviewElement) {
        codePreviewElement.remove();
      }
      codePreviewContainer.innerHTML = this.getPreviewOuterHTML(innerHTML);

      window.Prism.highlightAll();
    }, 1000)
  }
};
